import { Injectable } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { ConfirmationDialogComponent, ConfirmationDialogType } from "@comunicare/comunicare-dashboard-modals";
import {
  Appointment,
  IAppointment,
  ICommunication,
  IDataSourceBackendParam,
  IFilter,
  IPatientAppointement,
  ISchedule,
  ITeleconsultationInfo,
  SMS_TYPE,
  STATUS_ENTITY,
  TELECONSULTATION_PERIOD_KEY,
} from "@comunicare/comunicare-interfaces";
import { IAppointmentService } from "@comunicare/comunicare-interfaces-dashboard-services";
import { TranslateService } from "@ngx-translate/core";
import { forkJoin, Observable } from "rxjs";
import { first, map } from "rxjs/operators";
import { TeleConsultationDialogComponent } from "../components/tele-consultation-dialog/tele-consultation-dialog.component";
import { AppointmentApiService } from "./api/appointment-api.service";
import { SessionService } from "./session.service";
import { UserService } from "./user.service";

export const SYSTEM_JITSI = "meetjitsi";
export const SYSTEM_JITSI_PRACTITIONER = "meetjitsi_practitioner";

@Injectable({
  providedIn: "root",
})
export class AppointmentService implements IAppointmentService {
  constructor(
    private appointmentApiService: AppointmentApiService,
    private sessionService: SessionService,
    private translateService: TranslateService,
    private dialog: MatDialog,
    private userService: UserService
  ) {}

  public list(): Observable<Appointment[]> {
    return this.appointmentApiService.list().pipe(
      map((values) => {
        return values.map((v) => new Appointment(v));
      })
    );
  }

  public getPatientAppointement(name: string): Observable<IPatientAppointement[]> {
    return this.appointmentApiService.getPatientAppointement(name);
  }

  public listVisioAppointements(patientId: string, allAppointments?: boolean): Observable<Appointment[]> {
    return this.appointmentApiService.listVisioAppointements(patientId, allAppointments).pipe(
      map((values) => {
        return values.filter((app) => app.entityStatus.indexOf(STATUS_ENTITY.ACTIVE) >= 0).map((v) => new Appointment(v));
      })
    );
  }

  /**
   * delete an appointement and the refresh view
   */
  public deleteApp(app: Appointment): Observable<unknown> {
    const i = app.entityStatus.indexOf(STATUS_ENTITY.ACTIVE);
    app.entityStatus[i] = STATUS_ENTITY.CANCELLED;
    if (app.unknowPatientData) {
      app.unknowPatientData.smsMailSent = false;
      app.unknowPatientData.smsType = SMS_TYPE.DELETE;
    }
    return this.appointmentApiService.update(app.interface);
  }

  public createVisioAppointement(app: IAppointment): Observable<Appointment> {
    return this.appointmentApiService.createVisioAppointement(app);
  }

  public updateVisioAppointement(app: IAppointment): Observable<IAppointment> {
    return this.appointmentApiService.update(app);
  }

  public getTeleconsultations(
    params: IDataSourceBackendParam & { appointmentPeriod?: TELECONSULTATION_PERIOD_KEY }
  ): Observable<ITeleconsultationInfo[]> {
    return this.appointmentApiService.getTeleconsultations(params);
  }

  public getTeleconsultationsCount(
    filters?: IFilter[],
    services?: string[],
    org?: string,
    appointmentPeriod?: TELECONSULTATION_PERIOD_KEY
  ): Observable<{ count: number }> {
    return this.appointmentApiService.getTeleconsultationsCount(filters, services, org, appointmentPeriod);
  }

  public getAgendaList(services: string[], org: string): Observable<ISchedule[]> {
    return this.appointmentApiService.getAgendaList(services, org);
  }

  /**
   * The video call can be started if the appointment is assigned to the practitioner,
   * or if he's allowed to assign it to himself (PUT appointment)
   * @param canUpdateAppointment the PUT appointment right, if already known (avoids checking the permissions for each row of a table)
   */
  public canStartVideoCall(appointment: Appointment, canUpdateAppointment?: boolean): boolean {
    return this.isAssignedToMe(appointment) || (canUpdateAppointment ?? this.userService.isAuthorizedSync(null, "appointment", "PUT"));
  }

  public startVideoCall(appointment: Appointment): void {
    if (!this.canStartVideoCall(appointment)) {
      return;
    }
    if (!this.isAssignedToMe(appointment)) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message: this.translateService.instant("table.autoAssignTeleconsultation"),
          type: ConfirmationDialogType.CONFIRM,
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.autoAssignAppointments([appointment])
            .pipe(first())
            .subscribe((appointments) => {
              this.openTeleconsultationDialog(new Appointment(appointments[0]));
            });
        }
      });
    } else {
      this.openTeleconsultationDialog(appointment);
    }
  }

  private isAssignedToMe(appointment: Appointment): boolean {
    return (
      Boolean(appointment.participantPractitionerRef) &&
      appointment.participantPractitionerRef.reference === this.sessionService.account.caremateIdentifier
    );
  }

  private openTeleconsultationDialog(appointment: Appointment): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      appointment,
    };
    this.dialog.open(TeleConsultationDialogComponent, dialogConfig);
  }

  public autoAssignAppointments(appointments: Appointment[]): Observable<IAppointment[]> {
    const updatePromises: Observable<IAppointment>[] = [];
    appointments.forEach((a) => {
      const practitionerIndex = a.participant.findIndex((p) => p.participantType.text === Appointment.PRACTITIONER);
      if (practitionerIndex !== -1) {
        a.participant[practitionerIndex].actor = {
          reference: this.sessionService.account.caremateIdentifier,
          display: this.sessionService.account.displayName,
        };
        updatePromises.push(this.updateVisioAppointement(a));
      }
    });
    return forkJoin(updatePromises);
  }

  public resendMessageVisioAppointement(app: IAppointment): Observable<ICommunication[]> {
    return this.appointmentApiService.resendMessageVisioAppointement(app);
  }
}
